import React from 'react'
import { FC } from 'react'
import styled, { css } from 'styled-components'
import { Text, TextSize, TextWeight } from '../text/textStyles'
import { media } from '../../theme/breakpoints'

type ButtonHeight = 'xSmall' | 'small' | 'large'
export type ButtonWidth =
    | 'xSmall'
    | 'small'
    | 'medium'
    | 'large'
    | 'hug'
    | 'fill'
type ButtonGap = 'small' | 'medium' | 'large'

// ACA-214: height and width used to be required with no default, so a button rendered
// without them got no size styles at all (12 admin buttons and the form-card Retry until
// !333/!341). small/hug is the pair every sized admin button already passes.
export const DEFAULT_BUTTON_HEIGHT: ButtonHeight = 'small'
export const DEFAULT_BUTTON_WIDTH: ButtonWidth = 'hug'

const heightStyles = {
    xSmall: css`
        height: 19px;
        min-height: 19px;
    `,
    small: css`
        height: 36px;
        min-height: 36px;
    `,
    large: css`
        height: 58px;
        min-height: 58px;
    `,
}

const widthStyles = {
    xSmall: css`
        min-width: 160px;
        width: fit-content;
    `,
    small: css`
        min-width: 240px;
        width: fit-content;
    `,
    medium: css`
        min-width: 320px;
        width: fit-content;
    `,
    large: css`
        min-width: 360px;
        width: fit-content;
    `,
    hug: css`
        width: fit-content;
    `,
    fill: css`
        width: 100%;
    `,
}

const gapStyles = {
    small: css`
        gap: 8px;
    `,
    medium: css`
        gap: 8px;
    `,
    large: css`
        gap: 16px;
    `,
}

// path sets the colors for the SVGs so it matches the text color
export const contentColorStyle = (color: string) => css`
    color: ${color};

    path[stroke^='#'] {
        stroke: ${color};
    }

    path[fill^='#'] {
        fill: ${color};
    }

    g[fill^='#'] {
        fill: ${color};
    }
`

const Container = styled.button<Props>`
    cursor: pointer;

    /* Browsers do not let a <button> inherit font-size, so without this it sits at the
       user-agent default (13.33px) and the em-sized label (small = 14/18em) renders ~10px.
       Pin it to the page size GlobalStyle sets on html/body so the label is 14px. */
    font-size: ${(p) => p.theme.baseFontSize}px;
    border-radius: 8px;
    border: 1px solid ${(p) => p.theme.colors.primary.default};

    padding-left: 24px;
    padding-right: 24px;

    &:hover {
        border: 1px solid ${(p) => p.theme.colors.primary.green700};
    }

    &:active {
        border: 1px solid ${(p) => p.theme.colors.primary.green900};
    }

    &:disabled {
        cursor: not-allowed;
        pointer-events: none;

        border: 1px solid ${(p) => p.theme.colors.tertiary.tertiary200};

        ${(p) => contentColorStyle(p.theme.colors.tertiary.tertiary600)}
    }

    ${(props) => heightStyles[props.height ?? DEFAULT_BUTTON_HEIGHT]}
    ${(props) => widthStyles[props.width ?? DEFAULT_BUTTON_WIDTH]}

    ${media.tabletAndBelow} {
        min-height: 44px;
        min-width: 44px;
    }
`

const Content = styled(Text)<{ $gap: ButtonGap }>`
    display: flex;
    justify-content: center;
    align-items: center;
    ${(p) => gapStyles[p.$gap]}
`

interface Props extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    /** Defaults to 'small' (36px), the standard button height. */
    height?: ButtonHeight
    /** Defaults to 'hug' (fits its content). */
    width?: ButtonWidth
    gap?: ButtonGap
    children: React.ReactNode
}

export const Button: FC<Props> = ({
    height = DEFAULT_BUTTON_HEIGHT,
    width = DEFAULT_BUTTON_WIDTH,
    gap = 'large',
    children,
    ...rest
}) => {
    const textSize = (): TextSize => {
        switch (height) {
            case 'xSmall':
                return 'xxSmall'
            case 'small':
                return 'xSmall'
            case 'large':
                return 'medium'
        }
    }

    const textWeight = (): TextWeight => {
        switch (height) {
            case 'xSmall':
                return 'medium'
            case 'small':
                return 'medium'
            case 'large':
                return 'bold'
        }
    }

    return (
        <Container height={height} width={width} {...rest}>
            <Content
                size={textSize()}
                $weight={textWeight()}
                $gap={gap}
                color="none"
            >
                {children}
            </Content>
        </Container>
    )
}
